import React, { useState } from 'react';
import { Container, Row, Col, Form, Button, Alert, Card, Spinner } from 'react-bootstrap';

/**
 * ExitCalculator - Public lead generation tool
 * No authentication required. Designed to be embedded in WordPress via iframe.
 *
 * Single path: essential info + monthly sales + profit margin (smart UX)
 */
function ExitCalculator() {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [analysis, setAnalysis] = useState('');
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    industry: '',
    exit_path: 'lifestyle',
    timeline: '',
    monthly_sales: '',
    margin_type: '', // 'known', 'estimate', or ''
    margin_value: '', // free text if they know it
    margin_range: '', // selected range if estimating
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleMarginTypeChange = (type) => {
    setFormData(prev => ({
      ...prev,
      margin_type: type,
      // Clear the other field when switching
      margin_value: type === 'known' ? prev.margin_value : '',
      margin_range: type === 'estimate' ? prev.margin_range : '',
    }));
  };

  const handleSubmit = async () => {
    if (!formData.email) {
      setError('Please enter your email address');
      return;
    }
    if (!formData.name) {
      setError('Please enter your name');
      return;
    }
    if (!formData.industry) {
      setError('Please enter your industry');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Build the margin info for the backend
      let margin_info = '';
      let margin_is_estimate = false;
      if (formData.margin_type === 'known' && formData.margin_value) {
        margin_info = formData.margin_value;
      } else if (formData.margin_type === 'estimate' && formData.margin_range) {
        if (formData.margin_range === 'not_sure') {
          margin_info = 'Unknown - use industry average';
          margin_is_estimate = true;
        } else {
          margin_info = formData.margin_range + ' (estimated)';
          margin_is_estimate = true;
        }
      }

      const payload = {
        email: formData.email,
        business_name: formData.name,
        industry: formData.industry,
        exit_path: formData.exit_path,
        timeline: formData.timeline,
        monthly_revenue: formData.monthly_sales,
        margin_info,
        margin_is_estimate,
        source: 'exit_calculator'
      };

      const response = await fetch('/api/exit-calculator/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (data.success) {
        setSubmitted(true);
        setEmailSent(data.email_sent || false);
        setAnalysis(data.analysis || '');
      } else {
        setError(data.error || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setError('Failed to submit. Please check your connection and try again.');
    }

    setLoading(false);
  };

  // Success screen
  if (submitted) {
    return (
      <Container className="py-4">
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            {analysis && (
              <Card className="shadow mb-4">
                <Card.Header className="text-center" style={{ background: '#0a0a0a', color: '#ddb75e' }}>
                  <h2 className="mb-0 py-2">Your Exit Strategy Analysis</h2>
                </Card.Header>
                <Card.Body className="p-4">
                  <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.7' }}>{analysis}</div>
                </Card.Body>
              </Card>
            )}
            <Card className="shadow text-center">
              <Card.Body className="p-5">
                {emailSent && (
                  <p className="text-muted mb-4">
                    A PDF copy has also been sent to <strong>{formData.email}</strong>
                  </p>
                )}
                <hr />
                <p className="mb-2">
                  <strong>Great. You've got your number. Now what?</strong>
                </p>
                <p className="text-muted mb-3">
                  Without a plan, you're drifting — a dinghy in the middle of the ocean with no paddle.
                  The Profit Map looks at your business twice: once as a marketer, once as an investor.
                  Then it tells you exactly what needs to happen.
                </p>
                <Button
                  variant="success"
                  size="lg"
                  href="https://app.aiprofitengineer.com"
                  target="_blank"
                  style={{ color: '#ffffff' }}
                >
                  Let's Map How to Get You There
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  const canSubmit = formData.name && formData.email && formData.industry && formData.monthly_sales;

  return (
    <Container className="py-4">
      <Row className="justify-content-center">
        <Col md={10} lg={7}>
          <Card className="shadow">
            <Card.Body className="p-4">
              <h2 className="text-center mb-2">Free Exit Strategy Calculator</h2>
              <p className="text-center text-muted mb-4">
                Get AI-powered recommendations for your business exit targets
              </p>

              {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

              <Form.Group className="mb-3">
                <Form.Label><strong>Your Name</strong></Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g., Sarah Johnson"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label><strong>Email Address</strong></Form.Label>
                <Form.Control
                  type="email"
                  placeholder="your@email.com"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                />
                <Form.Text className="text-muted">We'll send your analysis here</Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label><strong>Industry</strong></Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g., SaaS, Consulting, E-commerce, Coaching"
                  value={formData.industry}
                  onChange={(e) => handleChange('industry', e.target.value)}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label><strong>Which exit path interests you?</strong></Form.Label>
                <Form.Select
                  value={formData.exit_path}
                  onChange={(e) => handleChange('exit_path', e.target.value)}
                >
                  <option value="lifestyle">Lifestyle Business - Keep forever, maximize profit & freedom</option>
                  <option value="build_to_sell">Build to Sell - Grow for 3-7 years then sell</option>
                  <option value="acquisition">Scale for Acquisition - Venture path, strategic exit</option>
                  <option value="legacy">Legacy Business - Pass to family/next generation</option>
                  <option value="not_sure">Not Sure Yet - Help me figure it out</option>
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label><strong>Timeline</strong></Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g., 5 years, By 2030, As soon as possible"
                  value={formData.timeline}
                  onChange={(e) => handleChange('timeline', e.target.value)}
                />
                <Form.Text className="text-muted">When do you want to reach your goal?</Form.Text>
              </Form.Group>

              <hr className="my-4" />

              <Form.Group className="mb-3">
                <Form.Label><strong>Average Monthly Sales</strong></Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g., $25,000 or Pre-revenue"
                  value={formData.monthly_sales}
                  onChange={(e) => handleChange('monthly_sales', e.target.value)}
                />
                <Form.Text className="text-muted">Your current gross revenue per month (or "Pre-revenue" if just starting)</Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label><strong>Profit Per Sale or Margin</strong></Form.Label>
                <div className="d-flex gap-2 mb-2">
                  <Button
                    variant={formData.margin_type === 'known' ? 'dark' : 'outline-secondary'}
                    size="sm"
                    onClick={() => handleMarginTypeChange('known')}
                  >
                    I know my margin
                  </Button>
                  <Button
                    variant={formData.margin_type === 'estimate' ? 'dark' : 'outline-secondary'}
                    size="sm"
                    onClick={() => handleMarginTypeChange('estimate')}
                  >
                    I'm not sure
                  </Button>
                </div>

                {formData.margin_type === 'known' && (
                  <Form.Control
                    type="text"
                    placeholder="e.g., $200/sale, 40% margin, $450/month/subscriber"
                    value={formData.margin_value}
                    onChange={(e) => handleChange('margin_value', e.target.value)}
                    className="mt-2"
                  />
                )}

                {formData.margin_type === 'estimate' && (
                  <div className="mt-2">
                    <Form.Text className="text-muted d-block mb-2">
                      After paying all your bills (not yourself), roughly what percentage is left over?
                    </Form.Text>
                    <div className="d-flex flex-wrap gap-2">
                      {[
                        { value: 'Under 10%', label: 'Under 10%' },
                        { value: '10-25%', label: '10-25%' },
                        { value: '25-50%', label: '25-50%' },
                        { value: '50%+', label: '50%+' },
                        { value: 'not_sure', label: 'Not sure' },
                      ].map((opt) => (
                        <Button
                          key={opt.value}
                          variant={formData.margin_range === opt.value ? 'dark' : 'outline-secondary'}
                          size="sm"
                          onClick={() => handleChange('margin_range', opt.value)}
                        >
                          {opt.label}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
              </Form.Group>

              <Button
                variant="success"
                size="lg"
                className="w-100 mt-3"
                onClick={handleSubmit}
                disabled={!canSubmit || loading}
                style={{ color: '#ffffff' }}
              >
                {loading ? (
                  <><Spinner size="sm" className="me-2" />Generating Your Analysis...</>
                ) : (
                  'Get My Exit Strategy'
                )}
              </Button>
            </Card.Body>
          </Card>

          <p className="text-center text-muted mt-3 small">
            Powered by <a href="https://app.aiprofitengineer.com" target="_blank" rel="noopener noreferrer">AI Profit Engineer</a>
          </p>
        </Col>
      </Row>
    </Container>
  );
}

export default ExitCalculator;
